import React, { useEffect, useRef, useState } from 'react';

import parse from 'html-react-parser';

import ProductDescription from './ProductDescription';
import { RelatedProductSection } from './RelatedProduct';
import ProductInformationSection from './ProductInformationSection';
import { ProductTechDetailsTable } from './ProductTechTable';
import { Container } from './styles';
import { useProduct } from './hooks/useProduct';

import {
  ICustomAttributeCms,
  IProductInfo,
  IRelatedProducts
} from '../../../../services/ProductMvp/types';

import { useParams } from 'react-router-dom';
import { EProductCustomAttributes } from './enum/EProductCustomAttribute';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { ICustomer } from '../../../../store/ducks/customers/types';
import { ProductBottomBar } from './ProductBottomBar';

const findCustomAttribute = (
  customAttributes: ICustomAttributeCms[],
  field: EProductCustomAttributes
) => {
  return customAttributes.find(
    (attribute) => attribute.attribute_code === field
  );
};

export const ProductInfoPage = ({
  emitterId,
  receiverId
}: {
  emitterId: string;
  receiverId: string;
}) => {
  const { sku } = useParams<{ sku: string }>();
  const { getProduct, getRelatedProducts, getProductArticle } = useProduct();
  const customersState = useSelector(
    (appState: ApplicationState) => appState.customers
  );
  const customer = customersState.customers.find(
    (customer) => customer.customerId === emitterId
  ) as ICustomer;
  const [productInfo, setProductInfo] = useState<IProductInfo | undefined>();
  const [relatedProductsState, setRelatedProducts] = useState<
    IRelatedProducts | undefined
  >();
  const [contextDescription, setContextDescription] = useState<
    JSX.Element[] | JSX.Element | string
  >([]);
  const [contentTecnologyDescription, setContentTecnologyDescription] =
    useState<JSX.Element[] | JSX.Element | string>([]);
  const [contentTecnologyImg, setContentTecnologyImg] = useState<
    JSX.Element[] | JSX.Element | string
  >([]);
  const [contentTechnical, setContentTechnical] = useState<
    JSX.Element[] | JSX.Element | string
  >([]);
  const [contentTechnicalImg, setContentTechnicalImg] = useState<
    JSX.Element[] | JSX.Element | string
  >([]);
  const [contentTechnicalTable, setContentTechnicalTable] = useState<
    JSX.Element[] | JSX.Element | string
  >([]);
  const [contentTechnicalSpecifications, setContentTechnicalSpecifications] =
    useState<JSX.Element[] | JSX.Element | string>([]);
  const [gpdValue, setGpdValue] = useState('');

  const navigateToBlock = (blockId: string) =>
    document
      .getElementById(blockId)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const product = await getProduct({
          sku,
          shipTo: receiverId,
          soldTo: emitterId
        });
        const urlPdp = product.custom_attributes.find(
          (attribute) =>
            attribute.attribute_code === EProductCustomAttributes.UrlPdp
        )?.value;
        const sapGpd = product.custom_attributes.find(
          (attribute) =>
            attribute.attribute_code === EProductCustomAttributes.DescriptionGpd
        )?.value;
        setGpdValue(String(sapGpd).trim().toLowerCase());
        const gpd = String(urlPdp?.length ? urlPdp : sapGpd)
          .trim()
          .replace(/[_\s]+/g, '-')
          .toLowerCase();
        const productArticle = await getProductArticle(gpd);

        const relatedProducts = await getRelatedProducts({
          sku,
          shipTo: receiverId,
          soldTo: emitterId
        });

        const articles: ICustomAttributeCms[] =
          productArticle?.custom_attributes.filter(
            (item) =>
              item.attribute_code === EProductCustomAttributes.Description ||
              item.attribute_code ===
                EProductCustomAttributes.TechnologyDescription ||
              item.attribute_code ===
                EProductCustomAttributes.TechnicalSpecificationsDescription ||
              item.attribute_code ===
                EProductCustomAttributes.TechnicalSpecificationsTable ||
              item.attribute_code ===
                EProductCustomAttributes.TechnicalDetailsImage ||
              item.attribute_code ===
                EProductCustomAttributes.TechnicalDetailsDescription ||
              item.attribute_code === EProductCustomAttributes.TechnologyImage
          ) ?? [];
        const description = findCustomAttribute(
          articles,
          EProductCustomAttributes.Description
        );

        if (description?.value) {
          const htmlDescription = parse(description.value);
          setContextDescription(htmlDescription);
        }

        const tecnology = findCustomAttribute(
          articles,
          EProductCustomAttributes.TechnologyDescription
        );

        if (tecnology?.value) {
          const html = parse(tecnology.value);
          setContentTecnologyDescription(html);
        }

        const tecnologyImg = findCustomAttribute(
          articles,
          EProductCustomAttributes.TechnologyImage
        );
        if (tecnologyImg?.value) {
          const html = parse(tecnologyImg.value);
          setContentTecnologyImg(html);
        }

        const technical = findCustomAttribute(
          articles,
          EProductCustomAttributes.TechnicalDetailsDescription
        );
        if (technical?.value) {
          const html = parse(technical.value);
          setContentTechnical(html);
        }

        const technicalImg = findCustomAttribute(
          articles,
          EProductCustomAttributes.TechnicalDetailsImage
        );
        if (technicalImg?.value) {
          const html = parse(technicalImg.value);
          setContentTechnicalImg(html);
        }

        const technicalTable = findCustomAttribute(
          articles,
          EProductCustomAttributes.TechnicalSpecificationsTable
        );
        if (technicalTable?.value) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(technicalTable.value, 'text/html');
          const table = doc.querySelector('table');
          if (table) {
            if (!table.querySelector('thead')) {
              const thead = doc.createElement('thead');
              const firstRow = table.querySelector('tr');
              if (firstRow) {
                thead.appendChild(firstRow);
                table.insertBefore(thead, table.firstChild);
              }
            }
            setContentTechnicalTable(parse(doc.body.innerHTML));
          }
        }

        const technicalSpecificationsDescription = articles.find(
          (article) =>
            article.attribute_code === 'technical_specifications_description'
        );
        if (technicalSpecificationsDescription?.value) {
          const html = parse(technicalSpecificationsDescription.value);
          setContentTechnicalSpecifications(html);
        }

        setProductInfo(product);
        setRelatedProducts(relatedProducts);
      } catch (error) {
        console.error('Error fetching product info:', error);
      }
    };

    const customerLoading =
      customersState.loading ||
      !!customersState.loadingPurchaseReceivers ||
      !!customersState.loadingProductDescription ||
      !!customersState.loadingBlocks ||
      !!customersState.loadingCustomerById;

    if (!customerLoading && customer?.productsDescriptionResponse) {
      fetchProducts();
    }
  }, [sku]);

  return (
    <Container>
      <ProductDescription
        product={productInfo}
        title={gpdValue}
        content={contextDescription}
        detailsLink={{
          show: contentTechnical.toString().length > 0,
          navigateTo: () => navigateToBlock('detalhes-tecnicos')
        }}
        tecnologyLink={{
          show: contentTecnologyDescription.toString().length > 0,
          navigateTo: () => navigateToBlock('tecnologia')
        }}
        specificationsLink={{
          show: contentTechnicalTable.toString().length > 0,
          navigateTo: () => navigateToBlock('especificacoes-tecnicas')
        }}
      />
      {relatedProductsState?.related &&
        relatedProductsState?.related.length > 0 && (
          <RelatedProductSection
            relatedProducts={relatedProductsState}
            gpdValue={gpdValue}
          />
        )}
      {contentTecnologyDescription.toString().length > 0 && (
        <div id="tecnologia">
          <ProductInformationSection
            contentText={contentTecnologyDescription}
            contentImg={contentTecnologyImg}
            productImage={productInfo?.media_gallery_entries[0]?.file ?? ''}
            title="Tecnologia"
            imagePosition="right"
          />
        </div>
      )}
      {contentTechnicalTable.toString().length > 0 && (
        <div id="especificacoes-tecnicas">
          <ProductTechDetailsTable
            productText={contentTechnicalSpecifications}
            table={contentTechnicalTable}
          />
        </div>
      )}
      {contentTechnical.toString().length > 0 && (
        <div id="detalhes-tecnicos">
          <ProductInformationSection
            title="Detalhes técnicos"
            contentText={contentTechnical}
            contentImg={contentTechnicalImg}
            productImage={productInfo?.media_gallery_entries[0]?.file ?? ''}
            imagePosition="left"
          />
        </div>
      )}
      <ProductBottomBar
        littleContent={
          contextDescription.toString().length == 0 && !productInfo
        }
      />
    </Container>
  );
};
